import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["use", "inputRef", "styled", "error", "resize", "cols", "rows", "onChange", "onBlur", "onFocus", "onKeyDown", "onKeyUp", "onKeyPress", "disabled", "autoFocus", "placeholder"];
import { useContext, forwardRef, memo } from 'react';
import ShareInput from '../ShareInput/ShareInput';
import { FieldsetContext } from '../../context/FieldsetContext';
import styled from 'foundations-theming/utils';
import { setUiTransition, setInputAppearance } from './TextArea.helpers.ts';
import { uiFocusObject } from '../../utils/color';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { useMatchTextAreaContext } from '../MatchTextArea/MatchTextAreaContext';
import { useExpandingTextAreaContext } from '../ExpandingTextArea/ExpandingTextAreaContext';
import { useCopyTextAreaContext } from '../CopyTextArea/CopyTextAreaContext';
import { useIsDisplayOptimized } from '../../context/DisplayOptimizedContext';
import usePlaceholder from '../../../hooks/usePlaceholder';
import { ControllableNext as Controllable } from '../Controllable/ControllableNext';
import { jsx as _jsx } from "react/jsx-runtime";
export const StyledTextArea = styled.textarea.withConfig({
  displayName: "TextArea__StyledTextArea",
  componentId: "sc-1jw551m-0"
})(({
  $isCode,
  $unstyled,
  $use,
  $error,
  rows,
  $resize,
  cols,
  $fieldsize,
  $readOnly,
  $isBgTransparent,
  $matchTextAreaSize,
  theme,
  $height,
  $maxHeight,
  $overflow,
  $cssStyles,
  $isDisplayOptimized
}) => {
  const $hasMinimalStyling = $isDisplayOptimized || $unstyled;
  return Object.assign({
    color: $isCode ? theme.color['text-core-default'] : theme.color['icon-core-default']
  }, setInputAppearance(theme), $isCode && {
    fontFamily: theme.typography['code-100'].fontFamily,
    fontWeight: theme.typography['code-100'].fontWeight,
    backgroundColor: theme.color['fill-tertiary-disabled']
  }, {
    paddingInline: $isDisplayOptimized ? theme.space['0'] : theme.space['300'],
    paddingBlock: theme.space['200']
  }, setUiTransition(), {
    height: 'auto'
  }, ($hasMinimalStyling || $isBgTransparent) && !$isCode ? {
    backgroundColor: 'transparent !important'
  } : {}, $use === 'on-dark' && !$error && !$hasMinimalStyling && {
    backgroundColor: theme.color['fill-field-default-alt']
  }, $error ? {
    borderColor: `${theme.color['border-alert-default']} !important`,
    boxShadow: `0 0 0 1px ${theme.color['border-alert-default']} !important`
  } : {}, $isCode ? {
    borderColor: `${theme.color['border-core-default']} !important`
  } : {}, $hasMinimalStyling && {
    borderColor: 'transparent !important',
    boxShadow: 'none !important'
  }, $isDisplayOptimized && {
    paddingBlock: theme.space['100'],
    border: 'none',
    fontSize: 'var(--trellis-typography-body-100-font-size)',
    lineHeight: '22px'
  }, $matchTextAreaSize === 'md' && {
    fontSize: theme.typography['body-200'].fontSize
  }, $matchTextAreaSize === 'xxl' && {
    fontSize: '32px',
    lineHeight: '40px'
  }, {
    minHeight: $isCode ? '155px' : rows || $fieldsize === 'small' || $isDisplayOptimized ? '0' : theme.space['1000'],
    resize: !$resize && 'none' || $resize === 'horizontal' && 'horizontal' || $resize === 'vertical' && 'vertical'
  }, !$resize && {
    display: 'inline-block'
  }, cols && cols > 0 && {
    width: 'auto'
  }, $fieldsize === 'small' && Object.assign({}, theme.typography['body-100'], {
    height: theme.space['800'],
    paddingBottom: theme.space['100'],
    paddingTop: theme.space['100'],
    paddingLeft: theme.space['200'],
    paddingRight: theme.space['200']
  }), {
    '&:focus': Object.assign({
      outline: 0
    }, !$readOnly && !$hasMinimalStyling && uiFocusObject, $error ? !$hasMinimalStyling && {
      boxShadow: `0 0 1px 2px ${theme.color['border-alert-default']} !important`
    } : {}),
    '&:disabled': {
      backgroundColor: theme.color['fill-field-disabled'],
      borderColor: theme.color['border-core-disabled'],
      boxShadow: 'none !important',
      color: theme.color['border-core-default'],
      cursor: 'not-allowed'
    },
    '&[readonly]': {
      '&:not([aria-expanded="true"])': {
        backgroundColor: theme.color['fill-field-disabled'],
        borderColor: theme.color['border-core-disabled'],
        boxShadow: 'none'
      }
    },
    '&::-moz-placeholder': {
      fontWeight: theme.typography['body-300'].fontWeight,
      opacity: 1,
      color: $hasMinimalStyling && $use === 'on-dark' ? theme.color['text-core-subtle'] : $use === 'on-dark' ? theme.color['text-core-subtle'] : theme.color['text-core-subtle']
    },
    '&:-ms-input-placeholder': {
      fontWeight: theme.typography['body-300'].fontWeight,
      color: $hasMinimalStyling && $use === 'on-dark' ? theme.color['text-core-subtle'] : $use === 'on-dark' ? theme.color['text-core-subtle'] : theme.color['text-core-subtle']
    },
    '&::-webkit-input-placeholder': {
      fontWeight: theme.typography['body-300'].fontWeight,
      color: $hasMinimalStyling && $use === 'on-dark' ? theme.color['text-core-subtle'] : $use === 'on-dark' ? theme.color['text-core-subtle'] : theme.color['text-core-subtle']
    }
  }, $height && {
    height: $height
  }, $maxHeight && {
    maxHeight: $maxHeight
  }, $overflow && {
    overflow: $overflow
  }, $cssStyles);
});
export const TextAreaComponent = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
      use,
      inputRef,
      styled: styledProp = true,
      error = false,
      resize = 'vertical',
      cols,
      rows,
      onChange,
      onBlur,
      onFocus,
      onKeyDown,
      onKeyUp,
      onKeyPress,
      disabled,
      autoFocus,
      placeholder
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const context = useContext(FieldsetContext);
  const matchTextAreaContext = useMatchTextAreaContext();
  const expandingTextAreaContext = useExpandingTextAreaContext();
  const copyTextAreaContext = useCopyTextAreaContext();
  const isDisplayOptimized = useIsDisplayOptimized();
  const {
    size: fieldSize,
    disabled: fieldDisabled
  } = context;
  const {
    domProps,
    tokenProps
  } = filterProps(rest, {
    isTextArea: true
  });
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const computedDisabled = disabled || fieldDisabled;
  const computedPlaceholder = usePlaceholder(placeholder);
  return /*#__PURE__*/_jsx(StyledTextArea, Object.assign({
    $cssStyles: cssStyles,
    $error: error,
    $fieldsize: fieldSize,
    $height: (expandingTextAreaContext === null || expandingTextAreaContext === void 0 ? void 0 : expandingTextAreaContext.height) || (matchTextAreaContext === null || matchTextAreaContext === void 0 ? void 0 : matchTextAreaContext.height),
    $isBgTransparent: (matchTextAreaContext === null || matchTextAreaContext === void 0 ? void 0 : matchTextAreaContext.isBgTransparent) || (copyTextAreaContext === null || copyTextAreaContext === void 0 ? void 0 : copyTextAreaContext.isBgTransparent),
    $isCode: copyTextAreaContext === null || copyTextAreaContext === void 0 ? void 0 : copyTextAreaContext.isCode,
    $isDisplayOptimized: isDisplayOptimized,
    $maxHeight: expandingTextAreaContext === null || expandingTextAreaContext === void 0 ? void 0 : expandingTextAreaContext.maxHeight,
    $matchTextAreaSize: matchTextAreaContext === null || matchTextAreaContext === void 0 ? void 0 : matchTextAreaContext.size,
    $overflow: expandingTextAreaContext === null || expandingTextAreaContext === void 0 ? void 0 : expandingTextAreaContext.overflow,
    $resize: resize,
    $unstyled: !styledProp,
    $use: use,
    cols: cols,
    rows: rows,
    ref: inputRef || ref,
    onChange: onChange,
    onBlur: onBlur,
    onFocus: onFocus,
    onKeyDown: onKeyDown,
    onKeyUp: onKeyUp,
    onKeyPress: onKeyPress,
    disabled: computedDisabled,
    autoFocus: autoFocus,
    placeholder: computedPlaceholder
  }, domProps));
});
// TODO: Consider removing this defaultProps if/when we decide to augment the
// Controllable component to accept defaults in it's function signature.
// see: https://git.hubteam.com/HubSpot/foundations-components/issues/5233
/* eslint-disable-next-line hubspot-dev/no-default-props-assignment */
TextAreaComponent.defaultProps = {
  value: ''
};
export const TextArea = ShareInput(Controllable( /*#__PURE__*/memo(TextAreaComponent)));
TextArea.displayName = 'TextArea';